// pages/ExpensePurchaseOrder/EpoListTable.js
//
// R43 — replaces the 3000-line SearchTableEPO mega-component for the
// three AllTasks tabs (Mine / Awaiting / Subordinates). Per-column filter
// row matches the rest of EPO (R41). Each mode gets its own columns +
// action buttons.

import React, { useMemo, useState } from "react";
import { Badge, Pagination, PaginationItem, PaginationLink, Input } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ColumnFilterRow, applyColumnFilters, EmptyState } from "../../Components/Common/DashboardKit";
import { downloadEpoPdf } from "./epoToPdf";
// Round EPO-Final — legal-entity filter vocabulary + labels (one source).
import { EPO_COMPANY_FILTER_OPTIONS, epoCompanyFilterCodeOf, getEpoCompanyDisplayLabel } from "./epoCompanyBranding";
import api from "../../api";

const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};
const pick = (r, ...keys) => {
    for (const k of keys) {
        if (r != null && r[k] !== undefined && r[k] !== null) return r[k];
    }
    return undefined;
};

const StatusBadge = ({ status }) => {
    const s = String(status || "").toUpperCase();
    const color =
        s === "APPROVED" ? "success" :
        s === "DRAFT" ? "secondary" : s === "PENDING" ? "warning" :
        s === "DENIED" || s === "VOID" ? "danger" : "secondary";
    return <Badge color={color} pill style={{ fontSize: 11 }}>{s || "—"}</Badge>;
};

// Per-mode column defs. Each gets an "actions" type:"none" column at the end.
const buildColumns = (mode) => {
    const common = [
        { id: "epoNumber",   label: "EPO #",         type: "text",
          get: r => pick(r, "epoNumber", "ePONumber", "EPONumber", "EpoNumber") },
        { id: "createdDate", label: "Created",       type: "text",
          get: r => fmtDate(pick(r, "createdDate", "CreatedDate")) },
    ];
    const purpose = { id: "purpose", label: "Purpose", type: "text",
                      get: r => pick(r, "epoPurpose", "EpoPurpose", "epoRemark", "EpoRemark") };
    const createdBy = { id: "createdBy", label: "Created By", type: "text",
                        get: r => pick(r, "createdBy", "CreatedBy", "createdByName", "CreatedByName") };
    const status = {
        id: "status", label: "Status", type: "select",
        options: [
            { value: "APPROVED", label: "Approved" },
            { value: "PENDING",  label: "Pending"  },
            // Rounds 1–2 batch — DRAFT lifecycle.
            { value: "DRAFT",    label: "Draft"    },
            { value: "DENIED",   label: "Denied"   },
            { value: "VOID",     label: "Void"     },
        ],
        get: r => String(pick(r, "status", "Status") || "").toUpperCase(),
    };
    const approxTotal = { id: "approxTotal", label: "Approx. Total", type: "text",
                          get: r => {
                              const v = pick(r, "approxTotal", "ApproxTotal");
                              return v != null ? String(v) : "";
                          }};
    const received = { id: "received", label: "Received", type: "boolean",
                       get: r => !!pick(r, "received", "Received") };
    const paid = { id: "paid", label: "Paid", type: "boolean",
                   get: r => !!pick(r, "paid", "Paid") };
    // Round EPO-Final — the EPO's OWN legal entity (NOT the vendor).
    // get() returns the canonical FILTER code so the select filter matches
    // exactly; renderCell shows the friendly label. NULL rows = LEGACY.
    const owningCompany = {
        id: "owningCompany", label: "Legal Entity", type: "select",
        options: EPO_COMPANY_FILTER_OPTIONS,
        get: r => epoCompanyFilterCodeOf(pick(r, "owningCompany", "OwningCompany")),
    };
    const actions = { id: "actions", label: "", type: "none" };

    if (mode === "awaiting") {
        return [...common, createdBy, purpose, approxTotal, status, actions];
    }
    if (mode === "subs") {
        return [...common, createdBy, purpose, approxTotal, status, actions];
    }
    // mode === "mine" (default)
    // Round EPO-Final — Legal Entity column on the personal list.
    return [...common, purpose, owningCompany, approxTotal, status, received, paid, actions];
};

// Action button stripes per mode.
// Batch FE-P10: every row in every mode now gets a "Download" link that
// generates the EPO PDF client-side via epoToPdf.downloadEpoPdf. No BE
// changes — the PDF is rendered in the browser via jsPDF (already used
// by CreateEpoForm for the notify/notify-change email attachments).
const DownloadPdfLink = ({ epoNum }) => {
    const [busy, setBusy] = useState(false);
    const onClick = async () => {
        if (!epoNum || busy) return;
        try {
            setBusy(true);
            await downloadEpoPdf(epoNum, api);
        } catch (err) {
            // Surface failures inline rather than silently dropping them.
            // Most likely cause: API auth expired or EPO not found.
            // eslint-disable-next-line no-alert
            alert(`Could not generate PDF for EPO #${epoNum}: ${err?.message || err}`);
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            type="button"
            className="btn btn-link btn-sm p-0 ms-2"
            disabled={busy}
            title={`Download EPO #${epoNum} as PDF`}
            onClick={onClick}
        >
            {busy ? "Downloading…" : "Download"}
        </button>
    );
};

const ActionButtons = ({ row, mode, onView, onEdit, onDuplicate, onApprove, onVoid, onSubmit }) => {
    const link = "btn btn-link btn-sm p-0";
    const epoNum = pick(row, "epoNumber", "EpoNumber", "ePONumber");
    if (mode === "awaiting") {
        return (
            <span className="text-nowrap">
                {onView && (
                    <button type="button" className={`${link} me-2`} onClick={() => onView(row)}>View</button>
                )}
                {onApprove && (
                    <button type="button" className="btn btn-success btn-sm me-2"
                            style={{ paddingTop: 1, paddingBottom: 1 }}
                            onClick={() => onApprove(row)}>Approve</button>
                )}
                {onVoid && (
                    <button type="button" className="btn btn-danger btn-sm"
                            style={{ paddingTop: 1, paddingBottom: 1 }}
                            onClick={() => onVoid(row)}>Reject</button>
                )}
                <DownloadPdfLink epoNum={epoNum} />
            </span>
        );
    }
    if (mode === "subs") {
        return (
            <span className="text-nowrap">
                {onView && (
                    <button type="button" className={link} onClick={() => onView(row)}>View</button>
                )}
                <DownloadPdfLink epoNum={epoNum} />
            </span>
        );
    }
    // mine
    // R45 — Edit is gated on row.isEditable from the BE. That flag is true when:
    //   • the user is finance / babu / IT-Admin (regardless of EPO state), OR
    //   • the user is the creator AND the EPO is in PENDING state AND it has not
    //     yet entered the approval workflow (no row in EpoApprovals).
    // Once submitted for approval, regular users see View + Duplicate only.
    const canEdit = !!pick(row, "isEditable", "IsEditable");
    return (
        <span className="text-nowrap">
            {onView && (
                <button type="button" className={`${link} me-2`} onClick={() => onView(row)}>View</button>
            )}
            {onEdit && canEdit && (
                <button type="button" className={`${link} me-2`} onClick={() => onEdit(row)}>Edit</button>
            )}
            {/* Rounds 1–2 batch — creator-visible Submit for DRAFTs and legacy
                stuck PENDING rows (IsSubmittable computed by the API; the
                server re-validates on POST /submit regardless). */}
            {onSubmit && !!pick(row, "isSubmittable", "IsSubmittable") && (
                <button type="button" className={`${link} me-2 text-success fw-semibold`}
                        onClick={() => onSubmit(row)}
                        title={`Submit EPO #${epoNum} for approval`}>Submit</button>
            )}
            {onDuplicate && (
                <button type="button" className={link}
                        onClick={() => onDuplicate(row)}
                        title={`Duplicate EPO #${epoNum}`}>Duplicate</button>
            )}
            <DownloadPdfLink epoNum={epoNum} />
        </span>
    );
};

// Cell renderer for non-action columns.
const renderCell = (row, col) => {
    const v = col.get(row);
    if (col.id === "status") return <StatusBadge status={v} />;
    if (col.id === "approxTotal") return fmtMoney(pick(row, "approxTotal", "ApproxTotal"));
    if (col.id === "received" || col.id === "paid") {
        return <span className="text-center d-inline-block w-100">{v ? <i className="ri-check-line text-success" /> : "—"}</span>;
    }
    if (col.id === "purpose") {
        return <span style={{ display: "inline-block", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>{v || "—"}</span>;
    }
    if (col.id === "epoNumber") return <strong>{v}</strong>;
    // Round EPO-Final — filter code -> friendly label for display.
    if (col.id === "owningCompany") return getEpoCompanyDisplayLabel(v);
    return v != null && v !== "" ? v : "—";
};

const EpoListTable = ({
    data,
    mode = "mine",   // "mine" | "awaiting" | "subs"
    onView,
    onEdit,
    onDuplicate,
    onSubmit,
    onApprove,
    onVoid,
    emptyTitle,
    emptyDescription,
    rowsPerPage = 25,
}) => {
    const navigate = useNavigate();
    const rows = Array.isArray(data) ? data : [];

    const [columnFilters, setColumnFilters] = useState({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(rowsPerPage);

    const columns = useMemo(() => buildColumns(mode), [mode]);

    // Client-side filter on loaded rows. AllTasks loads everything up front;
    // pagination is also client-side here (matches legacy behavior).
    const filtered = useMemo(
        () => applyColumnFilters(rows, columns, columnFilters),
        [rows, columns, columnFilters]
    );

    const total = filtered.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageRows = useMemo(
        () => filtered.slice((safePage - 1) * pageSize, safePage * pageSize),
        [filtered, safePage, pageSize]
    );

    // Reset to page 1 when filters or pageSize change (filtered length may shrink).
    const goToPage = (n) => {
        const t = Math.max(1, Math.min(totalPages, n));
        if (t !== safePage) setPage(t);
    };
    const onSizeChange = (sz) => {
        setPageSize(Number(sz) || 25);
        setPage(1);
    };

    // Reset to page 1 if filters change and current page would be empty.
    React.useEffect(() => {
        if (safePage !== page) setPage(safePage);
    }, [safePage, page]);

    // Windowed page numbers (max 7).
    const w = 7;
    let lo = Math.max(1, safePage - 3);
    let hi = Math.min(totalPages, lo + w - 1);
    lo = Math.max(1, hi - w + 1);
    const nums = [];
    for (let i = lo; i <= hi; i++) nums.push(i);

    if (rows.length === 0) {
        return (
            <EmptyState
                title={emptyTitle || "Nothing to show"}
                description={emptyDescription || ""}
            />
        );
    }

    return (
        <>
            <div className="table-responsive epo-sticky-head">
                <table className="table table-striped table-bordered mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            {columns.map((c) => (
                                <th key={c.id}
                                    className={c.id === "approxTotal" ? "text-end"
                                        : (c.id === "received" || c.id === "paid") ? "text-center"
                                        : ""}>
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                        <ColumnFilterRow
                            columns={columns}
                            filters={columnFilters}
                            onChange={setColumnFilters}
                        />
                    </thead>
                    <tbody>
                        {pageRows.map((row, idx) => {
                            const epoNum = pick(row, "epoNumber", "EpoNumber", "ePONumber");
                            return (
                                <tr key={epoNum ?? idx}>
                                    {columns.map((c) => {
                                        if (c.id === "actions") {
                                            return (
                                                <td key={c.id}>
                                                    <ActionButtons
                                                        row={row}
                                                        mode={mode}
                                                        onView={onView}
                                                        onEdit={onEdit}
                                                        onDuplicate={onDuplicate}
                                                        onSubmit={onSubmit}
                                                        onApprove={onApprove}
                                                        onVoid={onVoid}
                                                    />
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={c.id}
                                                className={c.id === "approxTotal" ? "text-end" : ""}>
                                                {renderCell(row, c)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {pageRows.length === 0 && (
                <div className="text-muted text-center py-3 small">
                    No rows match the current filters.
                </div>
            )}

            {/* Pager — client-side since AllTasks pre-loads everything. */}
            {total > 0 && (
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">Rows per page:</span>
                        <Input type="select" bsSize="sm" value={pageSize}
                            onChange={(e) => onSizeChange(e.target.value)} style={{ width: 80 }}>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                        </Input>
                        <span className="text-muted small">
                            Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, total)} of {total}
                            {total !== rows.length && ` (filtered from ${rows.length})`}
                        </span>
                    </div>
                    <Pagination size="sm" className="mb-0">
                        <PaginationItem disabled={safePage <= 1}><PaginationLink first onClick={() => goToPage(1)} /></PaginationItem>
                        <PaginationItem disabled={safePage <= 1}><PaginationLink previous onClick={() => goToPage(safePage - 1)} /></PaginationItem>
                        {nums.map((n) => (
                            <PaginationItem key={n} active={n === safePage}>
                                <PaginationLink onClick={() => goToPage(n)}>{n}</PaginationLink>
                            </PaginationItem>
                        ))}
                        <PaginationItem disabled={safePage >= totalPages}><PaginationLink next onClick={() => goToPage(safePage + 1)} /></PaginationItem>
                        <PaginationItem disabled={safePage >= totalPages}><PaginationLink last onClick={() => goToPage(totalPages)} /></PaginationItem>
                    </Pagination>
                </div>
            )}
        </>
    );
};

export default EpoListTable;
